/**
 * The live sales-call screen (ConversationPage), shared by every training
 * page. Training exists to get reps used to this exact layout and these exact
 * alerts, so the regions and the components that fill them are the live-call
 * ones, fed from the same Redux slices:
 *
 *   fixed header  TopBar + CombinedInfoBar (transcript | new inquiries | landmines)
 *   left 40%      `statusPanel` where the call shows "Audio Settings",
 *                 then SalesPlaybookPanel when the track has a playbook
 *   right 60%     ConversationFeed
 *   overlays      the CompetitorAlert stack (landmines live in the info bar)
 *
 * The main area's top margin is the MEASURED header height, as on the call,
 * so nothing hides behind the header as the transcript bar grows.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { RootState } from '../../store';
import { removeCompetitorAlert } from '../../store/slices/competitorSlice';
import { CombinedInfoBar } from '../CombinedInfoBar';
import CompetitorAlert from '../CompetitorAlert';
import ConversationFeed from '../ConversationFeed';
import SalesPlaybookPanel from '../SalesPlaybookPanel';
import TopBar from '../TopBar';

/** ConversationPage's starting guesses, replaced by measurement on mount. */
const INITIAL_HEADER_HEIGHT = 92;
const INITIAL_PANEL_HEIGHT = 600;
const MIN_PANEL_HEIGHT = 150;
/** space-y-2 between the status panel and the playbook panel. */
const LEFT_COLUMN_GAP = 8;

interface TrainingCallLayoutProps {
  onSignOut: () => void;
  /** Fills the box where the live call shows "Audio Settings". */
  statusPanel: React.ReactNode;
  showPlaybook: boolean;
  /** Enables ticking and notes in the playbook panel. */
  isSessionActive: boolean;
  /** Where the rep's ticks and notes go: Playbook Live sends them to its
   * session's checklist; without it (KB mode) they stay on screen. */
  onPlaybookAction?: (action: { updateType: string; [key: string]: any }) => void;
  /** Modals and other overlays rendered on top of the layout. */
  children?: React.ReactNode;
}

/**
 * KB training has no checklist, so its ticks stay on screen only. Playbook Live
 * passes its own handler (its socket's playbook_action frame). Either way the
 * panel gets a handler, which keeps it from persisting through a live-call
 * Recall socket that may still be open in this tab.
 */
const keepPlaybookActionLocal = (): void => undefined;

function useMeasuredHeight(ref: React.RefObject<HTMLElement | null>, initial: number): number {
  const [height, setHeight] = useState(initial);
  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const observer = new ResizeObserver(() => setHeight(element.offsetHeight));
    observer.observe(element);
    setHeight(element.offsetHeight);
    return () => observer.disconnect();
  }, [ref]);
  return height;
}

/**
 * The playbook panel fills what the left column has left after the status
 * panel, and a panel the rep left fully open keeps tracking that space; one
 * they dragged smaller is only ever clamped (same rule as ConversationPage).
 */
function usePlaybookPanelHeight(
  columnRef: React.RefObject<HTMLDivElement | null>,
  statusRef: React.RefObject<HTMLDivElement | null>
) {
  const [panelHeight, setPanelHeight] = useState(INITIAL_PANEL_HEIGHT);
  const [maxPanelHeight, setMaxPanelHeight] = useState(INITIAL_PANEL_HEIGHT);
  const lastMaxRef = useRef<number | null>(null);

  useEffect(() => {
    const column = columnRef.current;
    if (!column) return undefined;
    const updateMax = (): void => {
      const statusHeight = statusRef.current?.clientHeight ?? 0;
      const nextMax = Math.max(MIN_PANEL_HEIGHT, column.clientHeight - statusHeight - LEFT_COLUMN_GAP);
      const previousMax = lastMaxRef.current;
      setMaxPanelHeight(nextMax);
      setPanelHeight((current) =>
        previousMax === null || current >= previousMax ? nextMax : Math.min(current, nextMax)
      );
      lastMaxRef.current = nextMax;
    };
    const observer = new ResizeObserver(updateMax);
    observer.observe(column);
    // The status panel changes height between the headphone check and the
    // live session; the call's Audio Settings box never does.
    if (statusRef.current) observer.observe(statusRef.current);
    updateMax();
    return () => observer.disconnect();
  }, [columnRef, statusRef]);

  return { panelHeight, setPanelHeight, maxPanelHeight };
}

export const TrainingCallLayout: React.FC<TrainingCallLayoutProps> = ({
  onSignOut,
  statusPanel,
  showPlaybook,
  isSessionActive,
  onPlaybookAction = keepPlaybookActionLocal,
  children,
}) => {
  const dispatch = useDispatch();
  const competitorAlerts = useSelector((state: RootState) => state.competitor?.alerts ?? []);

  const fixedHeaderRef = useRef<HTMLDivElement>(null);
  const leftColumnRef = useRef<HTMLDivElement>(null);
  const statusPanelRef = useRef<HTMLDivElement>(null);
  const fixedHeaderHeight = useMeasuredHeight(fixedHeaderRef, INITIAL_HEADER_HEIGHT);
  const { panelHeight, setPanelHeight, maxPanelHeight } = usePlaybookPanelHeight(leftColumnRef, statusPanelRef);

  return (
    <div className="h-screen flex flex-col bg-surface-subtle overflow-hidden">
      <div ref={fixedHeaderRef} className="fixed top-0 left-0 right-0 z-50">
        <TopBar onSignOut={onSignOut} />
        <CombinedInfoBar />
      </div>

      <div className="fixed top-32 right-4 z-40 w-96 max-h-[80vh] overflow-y-auto space-y-2">
        {competitorAlerts.map((alert, index) => (
          <CompetitorAlert
            key={`competitor-alert-${index}`}
            alert={alert}
            onDismiss={() => dispatch(removeCompetitorAlert(index))}
          />
        ))}
      </div>

      <div className="flex-1 flex min-h-0 gap-2 px-2 pt-2 pb-2" style={{ marginTop: `${fixedHeaderHeight}px` }}>
        <div ref={leftColumnRef} className="w-2/5 flex flex-col min-h-0 space-y-2">
          <div ref={statusPanelRef} className="bg-surface rounded-lg shadow-md p-2" data-testid="training-status-panel">
            {statusPanel}
          </div>
          {showPlaybook && (
            <SalesPlaybookPanel
              height={panelHeight}
              onHeightChange={setPanelHeight}
              minHeight={MIN_PANEL_HEIGHT}
              maxHeight={maxPanelHeight}
              isSessionActive={isSessionActive}
              onPlaybookAction={onPlaybookAction}
            />
          )}
        </div>

        <div className="w-3/5 flex flex-col min-h-0">
          <ConversationFeed />
        </div>
      </div>

      {children}
    </div>
  );
};

export default TrainingCallLayout;
